import React, { useEffect, useMemo, useState } from 'react';
import { usePersonalizationContext } from '@contensis/personalization-react';
import PersonalizationPanelStyled, {
  PersonalizationAudiencesPanelStyled,
  PersonalizationPanelBodyStyled,
} from './PersonalizationPanel.styled';
import PanelWrapper from '@/components/panelWrapper/PanelWrapper';
import { PersonalizationPanelProps } from './PersonalizationPanel.types';
import Dropdown from './Dropdown';
import AudienceListing from './AudienceListing';
import { Audience } from './AudienceListing.types';
import { sortByStringInKey } from '@/utils/array';
import { IAudience, PersonalizationContext } from '@contensis/personalization';
import { Icons } from '@/components/icon/Icon.types';
import {
  DeveloperToolsHeadingStyled,
  DeveloperToolsSlidePanelStyled,
} from './PersonalizationDeveloperToolsPanel.styled';
import DeveloperToolsCards from './DeveloperToolsCards';
import { useToolbarNavigation } from '@/components/toolbar/Toolbar.utils';

const storage = new PersonalizationContext.Store({
  key: 'contensis-personalization-preview-toolbar-audiences',
  type: 'localStorage',
});

/** List of audiences that are active */
const mergeAudiencesState = (
  userAudiences: Audience[],
  manifestAudiences: IAudience[],
  isAudience: (id: string | string[]) => boolean
) => {
  const next: Audience[] = [];
  // Iterate userAudences from state/storage so we can maintain
  // the list the user has selected in the panel
  for (const userAudience of userAudiences) {
    const audienceInManifest = manifestAudiences.find(
      ma => ma.id === userAudience.id
    );
    // Add the user audience to the next list
    // If we don't have any manifest audiences yet, or...
    // this audience does not exist in the manifest audiences list
    if (!manifestAudiences.length || !audienceInManifest) {
      userAudience.isActive = false;
      userAudience.isDisabled = true;
      next.push(userAudience);
      // If the userAudience (from storage) is in the manifest audiences
    } else if (audienceInManifest) {
      userAudience.isDisabled = false; // Unset any disabled state
      // Check whether the userAudience (from storage) is active or not
      // So we can keep the previously selected userAudience in the list
      // but not have the list override the audiences updated since the
      // panel was closed last
      if (isAudience(userAudience.id)) userAudience.isActive = true;
      else {
        userAudience.isActive = false;
      }
      next.push(userAudience);
    }
  }

  // Add any further audiences from the manifest that are active right now
  // but are not in the next user audience list
  const newFromManifest = manifestAudiences
    .filter(
      // Filter manifest audiences where we can't find them in next and is active
      ma => !next.find(a => a.id === ma.id) && isAudience(ma.id)
    )
    .map(a => ({ ...a, isActive: true }));

  return [...next, ...newFromManifest].sort(sortByStringInKey('name'));
};

const PersonalizationPanel = ({
  title,
  onClose,
}: PersonalizationPanelProps) => {
  const [developerPanel, setDeveloperPanel] = useState(false);

  const { context, isAudience, manifest, toggleAudience } =
    usePersonalizationContext();

  /** Complete list of audiences from the manifest */
  const manifestAudiences = useMemo(
    () => manifest?.audiences ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manifest?.isReady, context.preview]
  );

  /**
   * List of audiences as state so we can maintain component state
   * separate from the context audiences that are currently active
   */
  const [userAudiences, setUserAudiences] = useState<Audience[]>(
    // Load any previously selected user audiences from storage
    storage.get() || []
  );

  /** Set initial audiences for listing */
  useEffect(() => {
    setUserAudiences(
      mergeAudiencesState(userAudiences, manifestAudiences, isAudience)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest?.isReady, context.preview]);

  /** Call the personalization action when the audiences state has changed */
  useEffect(() => {
    for (const audience of userAudiences) {
      if (audience.isRemoved) {
        if (audience.isActive) toggleAudience(audience.id);
        setUserAudiences(userAudiences.filter(ua => ua.id !== audience.id));
      } else {
        // Check is state audience different to active audiences
        const toggle = audience.isActive === !isAudience(audience.id);

        // Call the personalization action
        if (toggle) toggleAudience(audience.id);
      }
    }
    // Persist the selected user audiences between sessions
    storage.set(userAudiences);
  }, [isAudience, toggleAudience, userAudiences]);

  /** Track audiences dropdown state here so we can deactivate keyboard
   *  navigation in the main panel when the dropdown is in the open state */
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  // Activate arrow key navigation for all interactive elements in this panel containing the data-id attribute
  useToolbarNavigation('[data-id="panel-btn"]', !isDropdownOpen);

  return (
    <PanelWrapper>
      <PersonalizationPanelStyled className="personalization-panel">
        <PanelWrapper.Header title={title} onClose={onClose} />
        <PersonalizationPanelBodyStyled>
          <PersonalizationAudiencesPanelStyled
            className={developerPanel ? 'hidden' : 'visible'}
          >
            <Dropdown
              id="audience-dropdown"
              title="Add an audience"
              isOpen={isDropdownOpen}
              // only render the audiences that are not in the user audiences list below
              options={manifestAudiences.filter(
                a => !userAudiences.find(ua => ua.id === a.id)
              )}
              onSelect={id => {
                if (typeof id !== 'string') {
                  setIsDropdownOpen(!!id);
                } else {
                  const addAudience = manifestAudiences.find(a => a.id === id);
                  if (addAudience) {
                    // Add the selected audience into the user audiences list and
                    // mark it as active, the audience is toggled on in the context
                    // via the useEffect
                    setUserAudiences(
                      [
                        ...userAudiences,
                        { ...addAudience, isActive: true },
                      ].sort(sortByStringInKey('name'))
                    );
                  }
                  setIsDropdownOpen(false);
                }
              }}
            />
            <AudienceListing
              audiences={userAudiences}
              isPreview={context.preview}
              removeAudience={(id: string) => {
                setUserAudiences(
                  userAudiences.map(a => {
                    // Set a flag to remove so we can toggle the
                    // audience from the context in the useEffect
                    if (a.id === id) a.isRemoved = true;
                    return a;
                  })
                );
              }}
              toggleAudience={(id: string) => {
                setUserAudiences(
                  userAudiences.map(a => {
                    // Toggle the active flag so we can toggle the
                    // audience in the context via the useEffect
                    if (a.id === id) a.isActive = !a.isActive;
                    return a;
                  })
                );
              }}
            />
          </PersonalizationAudiencesPanelStyled>
          <DeveloperToolsSlidePanelStyled
            className={developerPanel ? 'open' : ''}
          >
            <DeveloperToolsHeadingStyled
              onClick={() => setDeveloperPanel(!developerPanel)}
            >
              <div>
                <Icons.cog color="#00304D" />
                <span>Developer tools</span>
              </div>
              <div>
                <button
                  id="devtool-toggle"
                  data-id="panel-btn"
                  onClick={() => setDeveloperPanel(!developerPanel)}
                >
                  <span className="focus-ring" />
                  {developerPanel ? 'Close' : 'Open'}
                </button>
              </div>
            </DeveloperToolsHeadingStyled>
            <DeveloperToolsCards
              className={developerPanel ? 'visible' : 'hidden'}
              context={context}
            />
          </DeveloperToolsSlidePanelStyled>
        </PersonalizationPanelBodyStyled>
      </PersonalizationPanelStyled>
    </PanelWrapper>
  );
};

export default PersonalizationPanel;
